import { Card, Row, Col } from 'reactstrap';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  TextAreaInput,
  Button,
  SelectOutlinedCreateable,
  OutlinedSelectOptionType,
  addToast,
  Icon,
} from '@octano/global-ui';
import { useGetJustificationStatusOptions } from '../../hooks/useGetJustificationStatusOptions';
import { postUpdateObservation } from '../../api';
import { useAttendancesTableFetch } from '../AttendancesTableLoader';
import { FileInput } from './FileInput';
import { useTexts } from '../../hooks/useTexts';
import { InfoWithTooltip } from './infoWithTooltip';
import dayjs from 'dayjs';

type Props = {
  row: any;
  teacherId: number;
};
export function CourseDetail({ row, teacherId }: Props) {
  const justificationDate =
    row.justificationDate && dayjs(row.justificationDate).format('DD/MM/YY');
  const observationDate =
    row.observationDate && dayjs(row.observationDate).format('DD/MM/YY');
  const justificationStatusOptions = useGetJustificationStatusOptions();
  const [fileIdsByRole, setFileIdsByRole] = useState<{
    teacherFilesIds: number[];
    adminFilesIds: number[];
  }>({
    teacherFilesIds: row.teacherFileGroup,
    adminFilesIds: row.adminFileGroup,
  });
  const texts = useTexts();
  const { control, watch } = useForm({
    defaultValues: row,
  });
  const [files] = watch([`validationComplementaryFilesIds_${row.id}`]);
  const { refresh } = useAttendancesTableFetch();
  const initialOption =
    justificationStatusOptions.find(
      (item) => item.value === row.justificationStatus,
    ) || justificationStatusOptions[0];
  const [loadingSave, setLoadingSave] = useState(false);
  const [observation, setObservation] = useState(row.adminObservation);
  const [
    justificationStatus,
    setJustificationStatus,
  ] = useState<OutlinedSelectOptionType>(initialOption);

  const complementaryFiles = useFieldArray({
    control,
    name: `validationComplementaryFilesIds_${row.id}`,
  });

  async function save() {
    setLoadingSave(true);
    const response = await postUpdateObservation(
      teacherId,
      row.id,
      justificationStatus.value.toString(),
      row.justification,
      observation,
      fileIdsByRole.teacherFilesIds,
      fileIdsByRole.adminFilesIds,
    );

    if (response.error) {
      addToast({
        text: texts.table.detail.failedToastLabel,
        icon: 'error',
        color: 'danger',
        autoClose: 3000,
      });
    }
    if (response.data) {
      addToast({
        text: texts.table.detail.successToastLabel,
        icon: 'check',
        color: 'success',
        autoClose: 3000,
      });
      refresh();
    }
    setLoadingSave(false);
  }
  return (
    <Card className=" mx-3">
      <Row className="mb-3">
        <Col>
          <div
            className="d-flex justify-content-end position-relative"
            style={{ top: '21px' }}
          >
            <Icon name="calendar" />
            <span className="ml-2">
              {justificationDate || texts.table.detail.noDateRegistered}
            </span>
          </div>
          <TextAreaInput
            label={texts.table.detail.justificationLabel}
            name="justification"
            value={row.justification}
            readOnly
          />
        </Col>
        <Col>
          <div
            className="d-flex justify-content-end position-relative over"
            style={{ top: '21px' }}
          >
            <Icon name="calendar" />
            <span className="ml-2">
              {observationDate || texts.table.detail.noDateRegistered}
            </span>
          </div>

          <TextAreaInput
            label={texts.table.detail.observationLabel}
            name="observation"
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
          />
          {row.observationAdminName && (
            <p
              className="d-flex align-items-center justify-content-start m-0 mt-1"
              style={{ gap: '8px' }}
            >
              <InfoWithTooltip show tooltipId={`${row.id}-tooltip`} />
              Ingresado por: {row.observationAdminName}
            </p>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <FileInput
            complementaryFiles={complementaryFiles}
            files={files}
            fileIdsByRole={fileIdsByRole}
            setFileIdsByRole={setFileIdsByRole}
          />
        </Col>
        <Col>
          <Row className="d-flex gap-2 mt-2">
            <Col xs={7}>
              <SelectOutlinedCreateable
                height={100}
                className="mt-2"
                label={texts.table.detail.justificationStatus}
                name="justificationStatus"
                value={justificationStatus}
                onChange={(e) => setJustificationStatus(e)}
                options={justificationStatusOptions}
              />
            </Col>
            <Col xs={5}>
              <Button
                className="mt-4"
                size="md"
                text={texts.table.detail.saveButton}
                fullwidth
                onClick={() => save()}
                disabled={loadingSave || !observation}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
}
